import IconChevronRight from "central-icons/IconChevronRight";
import {
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionContent,
    AccordionTrigger,
} from "@radix-ui/react-accordion";
import {
    type EnvironmentGitStatus,
    type FileChange,
    FileChange_ChangeType,
    EnvironmentPhase,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import { forwardRef, memo, useCallback, useImperativeHandle, useMemo, useRef, useState, type FC } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { cva } from "class-variance-authority";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { useEnvironmentGitDiff, type PlainEnvironment } from "@/queries/environment-queries";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { Text } from "@/components/podkit/typography/Text";
import { GitDiffView, type LineSelection } from "@/routes/environments/code-changes/GitDiffView";
import { extractNewContentFromHunks } from "@/routes/environments/code-changes/diff-utils";
import { MarkdownPreview } from "@/routes/environments/code-changes/MarkdownPreview";
import { effectiveState, showStart } from "@/routes/environments/phase";
import type { ReviewComment, PendingCommentSelection } from "@/components/ai/review/types";

// Re-export for backward compatibility
export type { PendingCommentSelection } from "@/components/ai/review/types";

export type FileChangeListRef = {
    addScrollListener: (listener: () => void) => void;
    removeScrollListener: (listener: () => void) => void;
    scrollToFile: (path: string) => void;
};

export type FileChangeListProps = {
    environment: PlainEnvironment;
    gitStatus: EnvironmentGitStatus;
    className?: string;
    openedFiles: string[];
    onOpenedFilesChange: (openedFiles: string[]) => void;
    /** Enable line selection for review comments */
    enableReviewComments?: boolean;
    /** Callback when lines are selected for a new comment */
    onLineSelectionForComment?: (selection: PendingCommentSelection) => void;
    /** All review comments for showing indicators */
    comments?: ReviewComment[];
    /** Callback when user clicks on a comment indicator in the diff */
    onCommentClick?: (comment: ReviewComment) => void;
    /** Currently selected lines for adding a comment */
    pendingCommentSelection?: PendingCommentSelection | null;
    /** Callback to add a comment */
    onAddComment?: (filePath: string, startLine: number, endLine: number, content: string) => void;
    /** Callback to cancel adding a comment */
    onCancelComment?: () => void;
};

export const FileChangeList = forwardRef<FileChangeListRef, FileChangeListProps>(
    (
        {
            environment,
            gitStatus,
            className,
            openedFiles,
            onOpenedFilesChange,
            enableReviewComments,
            onLineSelectionForComment,
            comments = [],
            onCommentClick,
            pendingCommentSelection,
            onAddComment,
            onCancelComment,
        },
        ref,
    ) => {
        const accordionRef = useRef<HTMLDivElement>(null);
        const disabled = showStart(environment);

        // Group comments by file path
        const commentsByFile = useMemo(() => {
            const map = new Map<string, ReviewComment[]>();
            for (const comment of comments) {
                const existing = map.get(comment.filePath) || [];
                existing.push(comment);
                map.set(comment.filePath, existing);
            }
            return map;
        }, [comments]);

        useImperativeHandle(
            ref,
            (): FileChangeListRef => ({
                addScrollListener: (listener) => {
                    accordionRef.current?.addEventListener("scroll", listener);
                },
                removeScrollListener: (listener) => {
                    accordionRef.current?.removeEventListener("scroll", listener);
                },
                scrollToFile: (path) => {
                    if (accordionRef.current) {
                        const element = document.getElementById(path);
                        element?.scrollIntoView();
                    }
                },
            }),
        );

        return (
            <Accordion
                ref={accordionRef}
                type="multiple"
                className={cn("w-full space-y-2", className)}
                value={openedFiles}
                onValueChange={onOpenedFilesChange}
                disabled={disabled}
            >
                {gitStatus.changedFiles?.map((file) => (
                    <FileChangeItem
                        key={file.path}
                        environment={environment}
                        file={file}
                        open={openedFiles.includes(file.path)}
                        disabled={disabled}
                        enableReviewComments={enableReviewComments}
                        onLineSelectionForComment={onLineSelectionForComment}
                        comments={commentsByFile.get(file.path) || []}
                        onCommentClick={onCommentClick}
                        pendingCommentSelection={pendingCommentSelection}
                        onAddComment={onAddComment}
                        onCancelComment={onCancelComment}
                    />
                ))}
            </Accordion>
        );
    },
);
FileChangeList.displayName = "FileChangeList";

export const FileChangeListSkeleton = ({ className }: { className?: string }) => {
    return (
        <div className={cn("flex w-full flex-col space-y-2", className)}>
            {Array.from({ length: 10 }).map((_, index) => (
                <SkeletonBlock className="h-12 w-full bg-surface-muted" key={index} ready={false} />
            ))}
        </div>
    );
};

const gitStatusDotVariants = cva(
    "flex size-4 items-center justify-center rounded-full bg-surface-accent text-xs font-medium select-none",
    {
        variants: {
            status: {
                [FileChange_ChangeType.UNSPECIFIED]: null,
                [FileChange_ChangeType.MODIFIED]: "bg-surface-accent text-content-muted",
                [FileChange_ChangeType.DELETED]: "bg-surface-warning-subtle text-content-destructive",
                [FileChange_ChangeType.ADDED]: "bg-surface-success-subtle text-content-success",
                [FileChange_ChangeType.UNTRACKED]: "bg-surface-warning-subtle text-content-warning",
                [FileChange_ChangeType.RENAMED]: "bg-surface-success-subtle text-content-success",
                [FileChange_ChangeType.COPIED]: "bg-surface-success-subtle text-content-success",
                [FileChange_ChangeType.UPDATED_BUT_UNMERGED]: "bg-surface-warning-subtle text-content-warning",
            },
        },
    },
);

const CHANGE_TYPE_LABEL = {
    [FileChange_ChangeType.UNSPECIFIED]: null,
    [FileChange_ChangeType.MODIFIED]: { symbol: "M", full: "modified" },
    [FileChange_ChangeType.DELETED]: { symbol: "D", full: "deleted" },
    [FileChange_ChangeType.ADDED]: { symbol: "A", full: "added" },
    [FileChange_ChangeType.UNTRACKED]: { symbol: "U", full: "untracked" },
    [FileChange_ChangeType.RENAMED]: { symbol: "R", full: "renamed" },
    [FileChange_ChangeType.COPIED]: { symbol: "C", full: "copied" },
    [FileChange_ChangeType.UPDATED_BUT_UNMERGED]: { symbol: "!", full: "unmerged" },
} as const;

export const GitStatusDot: FC<{ status: FileChange_ChangeType }> = (props) => {
    const label = CHANGE_TYPE_LABEL[props.status];
    if (!label) return null;

    return (
        <span aria-hidden="true" className={cn(gitStatusDotVariants({ status: props.status }))}>
            {label.symbol}
        </span>
    );
};

const isMarkdownFile = (path: string): boolean => {
    const lowerPath = path.toLowerCase();
    return lowerPath.endsWith(".md") || lowerPath.endsWith(".markdown");
};

type ViewMode = "diff" | "preview";

type FileChangeItemProps = {
    environment: PlainEnvironment;
    file: FileChange;
    open: boolean;
    disabled?: boolean;
    enableReviewComments?: boolean;
    onLineSelectionForComment?: (selection: PendingCommentSelection) => void;
    comments?: ReviewComment[];
    onCommentClick?: (comment: ReviewComment) => void;
    pendingCommentSelection?: PendingCommentSelection | null;
    onAddComment?: (filePath: string, startLine: number, endLine: number, content: string) => void;
    onCancelComment?: () => void;
};

const FileChangeItem = memo(
    ({
        environment,
        file,
        open,
        disabled,
        enableReviewComments,
        onLineSelectionForComment,
        comments = [],
        onCommentClick,
        pendingCommentSelection,
        onAddComment,
        onCancelComment,
    }: FileChangeItemProps) => {
        const fileName = useMemo(() => {
            return file.path.split("/").pop();
        }, [file.path]);

        const isMarkdown = isMarkdownFile(file.path);
        const [viewMode, setViewMode] = useState<ViewMode>("diff");

        const handleViewModeChange = (e: React.MouseEvent, mode: ViewMode) => {
            e.stopPropagation();
            setViewMode(mode);
        };

        return (
            <AccordionItem
                className={cn(
                    "overflow-clip rounded-lg border border-border-base bg-surface-primary",
                    disabled && "pointer-events-none opacity-50",
                )}
                id={file.path}
                value={file.path}
                disabled={disabled}
            >
                <AccordionHeader className={cn(!disabled && "hover:bg-surface-hover")}>
                    <AccordionTrigger
                        disabled={disabled}
                        className="group grid w-full grid-cols-[min-content_minmax(0,1fr)] grid-rows-[min-content_min-content] border-b border-b-transparent data-[state=open]:border-b-border-base data-[state=open]:bg-surface-muted"
                    >
                        <IconChevronRight className="col-span-1 row-span-2 m-2 size-4 self-center text-content-secondary transition-transform group-data-[state=open]:rotate-90" />
                        <div className="col-span-1 row-span-1 flex min-w-0 flex-row items-center justify-between self-end pr-2 pt-2">
                            <Text className="truncate text-base font-medium">{fileName}</Text>
                            <div className="flex items-center gap-2">
                                {isMarkdown && open && (
                                    <div className="flex rounded-md border border-border-base bg-surface-secondary text-xs">
                                        <button
                                            type="button"
                                            data-tracking-id="file-change-view-mode-diff"
                                            onClick={(e) => handleViewModeChange(e, "diff")}
                                            className={cn(
                                                "rounded-l-md px-2 py-0.5 transition-colors",
                                                viewMode === "diff"
                                                    ? "bg-surface-primary text-content-primary"
                                                    : "text-content-secondary hover:text-content-primary",
                                            )}
                                        >
                                            Diff
                                        </button>
                                        <button
                                            type="button"
                                            data-tracking-id="file-change-view-mode-preview"
                                            onClick={(e) => handleViewModeChange(e, "preview")}
                                            className={cn(
                                                "rounded-r-md px-2 py-0.5 transition-colors",
                                                viewMode === "preview"
                                                    ? "bg-surface-primary text-content-primary"
                                                    : "text-content-secondary hover:text-content-primary",
                                            )}
                                        >
                                            Preview
                                        </button>
                                    </div>
                                )}
                                <GitStatusDot status={file.changeType} />
                            </div>
                        </div>
                        <Text className="col-span-1 row-span-1 min-w-0 self-start text-wrap break-words pb-2 pr-4 text-start font-mono text-sm text-content-muted">
                            {file.path}
                        </Text>
                    </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent>
                    <FileChangeItemContent
                        environment={environment}
                        file={file}
                        open={open}
                        viewMode={viewMode}
                        isMarkdown={isMarkdown}
                        enableReviewComments={enableReviewComments}
                        onLineSelectionForComment={onLineSelectionForComment}
                        comments={comments}
                        onCommentClick={onCommentClick}
                        pendingCommentSelection={pendingCommentSelection}
                        onAddComment={onAddComment}
                        onCancelComment={onCancelComment}
                    />
                </AccordionContent>
            </AccordionItem>
        );
    },
);
FileChangeItem.displayName = "FileChangeItem";

type FileChangeItemContentProps = {
    environment: PlainEnvironment;
    file: FileChange;
    open: boolean;
    viewMode: ViewMode;
    isMarkdown: boolean;
    enableReviewComments?: boolean;
    onLineSelectionForComment?: (selection: PendingCommentSelection) => void;
    comments?: ReviewComment[];
    onCommentClick?: (comment: ReviewComment) => void;
    pendingCommentSelection?: PendingCommentSelection | null;
    onAddComment?: (filePath: string, startLine: number, endLine: number, content: string) => void;
    onCancelComment?: () => void;
};

const FileChangeItemContent = memo(
    ({
        environment,
        file,
        open,
        viewMode,
        isMarkdown,
        enableReviewComments,
        onLineSelectionForComment,
        comments,
        onCommentClick,
        pendingCommentSelection,
        onAddComment,
        onCancelComment,
    }: FileChangeItemContentProps) => {
        const envState = effectiveState(environment);
        const starting =
            envState.state === EnvironmentPhase.CREATING ||
            envState.state === EnvironmentPhase.STARTING ||
            envState.state === EnvironmentPhase.UPDATING;
        const { data: diffResult, isPending } = useEnvironmentGitDiff(environment.id, {
            path: file.path,
            enabled: open && !starting,
        });

        const markdownContent = useMemo(() => {
            if (!isMarkdown || !diffResult?.hunks) return null;
            return extractNewContentFromHunks(diffResult.hunks);
        }, [isMarkdown, diffResult?.hunks]);

        const handleLineSelect = useCallback(
            (selection: LineSelection) => {
                if (onLineSelectionForComment) {
                    onLineSelectionForComment({
                        filePath: file.path,
                        startLine: selection.startLine,
                        endLine: selection.endLine,
                    });
                }
            },
            [file.path, onLineSelectionForComment],
        );

        if (!open) return null;

        if (starting) {
            return (
                <div className="flex items-center gap-2 p-2 text-content-muted">
                    <IconSpinner className="animate-spin" size="sm" />
                    <Text className="text-sm text-content-muted">Waiting for environment to start…</Text>
                </div>
            );
        }

        if (isPending) {
            return (
                <div className="flex items-center gap-2 p-2 text-content-muted">
                    <IconSpinner className="animate-spin" size="sm" />
                    <Text className="text-sm text-content-muted">Loading file diff…</Text>
                </div>
            );
        }

        if (!diffResult) {
            return (
                <div className="flex items-center gap-2 p-2 text-content-muted">
                    <Text className="text-sm text-content-muted">Diff unavailable</Text>
                </div>
            );
        }

        if (viewMode === "diff") {
            const pendingForThisFile = pendingCommentSelection?.filePath === file.path ? pendingCommentSelection : null;
            return (
                <GitDiffView
                    hunks={diffResult.hunks}
                    filePath={file.path}
                    selectable={enableReviewComments}
                    onLineSelect={handleLineSelect}
                    comments={comments}
                    onCommentClick={onCommentClick}
                    pendingCommentSelection={pendingForThisFile}
                    onAddComment={
                        pendingForThisFile && onAddComment
                            ? (content) =>
                                  onAddComment(
                                      file.path,
                                      pendingForThisFile.startLine,
                                      pendingForThisFile.endLine,
                                      content,
                                  )
                            : undefined
                    }
                    onCancelComment={onCancelComment}
                />
            );
        }

        // Preview mode
        if (!markdownContent) {
            return (
                <div className="flex items-center gap-2 p-2 text-content-muted">
                    <Text className="text-sm text-content-muted">Preview unavailable</Text>
                </div>
            );
        }

        return (
            <div className="p-4">
                <MarkdownPreview content={markdownContent} className="prose prose-sm dark:prose-invert max-w-none" />
            </div>
        );
    },
);
FileChangeItemContent.displayName = "FileChangeItemContent";
